import aesjs from 'aes-js';

/**
 * Decrypt MP4 CENC AES-128-CTR (mis. Melolo) di client, lalu netralkan
 * box DRM supaya `<video>` native bisa memutar hasilnya tanpa EME.
 *
 * Port 1:1 dari player reference resmi QuickPlay (melolo_player.html).
 * Kunci diberikan sah oleh QuickPlay (layanan berbayar) via response
 * /video `drm.key`.
 *
 * Alur: parse ISO-BMFF (moov→trak→mdia→minf→stbl→senc), IV 8-byte per
 * sample dari senc, decrypt AES-CTR tiap sample (dukung subsample), lalu
 * rename box encv/enca/sinf/schm/senc/pssh dsb agar jadi codec biasa.
 */

function readU32(b: Uint8Array, o: number): number {
  return ((b[o]! << 24) | (b[o + 1]! << 16) | (b[o + 2]! << 8) | b[o + 3]!) >>> 0;
}
function readU64(b: Uint8Array, o: number): number {
  return readU32(b, o) * 0x100000000 + readU32(b, o + 4);
}
function readTag(b: Uint8Array, o: number): string {
  return String.fromCharCode(b[o]!, b[o + 1]!, b[o + 2]!, b[o + 3]!);
}
function writeTag(b: Uint8Array, o: number, tag: string): void {
  for (let i = 0; i < 4; i++) b[o + i] = tag.charCodeAt(i);
}

interface Box {
  offset: number;
  size: number;
  tag: string;
  bodyOff: number;
}

function iterBoxes(buf: Uint8Array, start: number, end: number): Box[] {
  const boxes: Box[] = [];
  let off = start;
  while (off + 8 <= end) {
    const size = readU32(buf, off);
    if (size < 8 || off + size > end) break;
    boxes.push({ offset: off, size, tag: readTag(buf, off + 4), bodyOff: off + 8 });
    off += size;
  }
  return boxes;
}

function findBox(buf: Uint8Array, s: number, e: number, tag: string): Box | null {
  for (const b of iterBoxes(buf, s, e)) if (b.tag === tag) return b;
  return null;
}

function walkPath(
  buf: Uint8Array,
  start: number,
  end: number,
  ...tags: string[]
): Box | null {
  let s = start;
  let e = end;
  let found: Box | null = null;
  for (const tag of tags) {
    found = null;
    for (const b of iterBoxes(buf, s, e)) {
      if (b.tag === tag) {
        found = b;
        s = b.bodyOff;
        e = b.offset + b.size;
        break;
      }
    }
    if (!found) return null;
  }
  return found;
}

interface Track {
  sampleOffs: number[];
  sizes: number[];
  ivs: Uint8Array[];
  subsamples: { clear: number; enc: number }[][];
}

function parseSampleTable(
  buf: Uint8Array,
  stblStart: number,
  stblEnd: number,
): Track | null {
  const find = (tag: string) => findBox(buf, stblStart, stblEnd, tag);
  const stsz = find('stsz');
  const stsc = find('stsc');
  const stco = find('stco');
  const co64 = find('co64');
  const senc = find('senc');
  if (!stsz || !stsc || (!stco && !co64)) return null;

  const defaultSz = readU32(buf, stsz.bodyOff + 4);
  const n = readU32(buf, stsz.bodyOff + 8);
  const sizes = new Array<number>(n);
  if (defaultSz === 0) {
    for (let i = 0; i < n; i++) sizes[i] = readU32(buf, stsz.bodyOff + 12 + i * 4);
  } else {
    sizes.fill(defaultSz);
  }

  let chunkOffs: number[];
  if (stco) {
    const cnt = readU32(buf, stco.bodyOff + 4);
    chunkOffs = new Array<number>(cnt);
    for (let i = 0; i < cnt; i++) chunkOffs[i] = readU32(buf, stco.bodyOff + 8 + i * 4);
  } else {
    const cnt = readU32(buf, co64!.bodyOff + 4);
    chunkOffs = new Array<number>(cnt);
    for (let i = 0; i < cnt; i++) chunkOffs[i] = readU64(buf, co64!.bodyOff + 8 + i * 8);
  }

  const ne = readU32(buf, stsc.bodyOff + 4);
  const entries: { firstChunk: number; spc: number }[] = [];
  for (let i = 0; i < ne; i++) {
    entries.push({
      firstChunk: readU32(buf, stsc.bodyOff + 8 + i * 12),
      spc: readU32(buf, stsc.bodyOff + 12 + i * 12),
    });
  }

  const sampleOffs: number[] = [];
  let si = 0;
  for (let ci = 0; ci < chunkOffs.length && si < n; ci++) {
    let spc = 1;
    for (const e of entries) {
      if (ci + 1 >= e.firstChunk) spc = e.spc;
      else break;
    }
    let cur = chunkOffs[ci]!;
    for (let k = 0; k < spc && si < n; k++) {
      sampleOffs.push(cur);
      cur += sizes[si]!;
      si++;
    }
  }

  const ivs: Uint8Array[] = [];
  const subsampleMap: { clear: number; enc: number }[][] = [];
  if (senc) {
    const flags = (buf[senc.bodyOff + 1]! << 16) | (buf[senc.bodyOff + 2]! << 8) | buf[senc.bodyOff + 3]!;
    const cnt = readU32(buf, senc.bodyOff + 4);
    let off = senc.bodyOff + 8;
    for (let i = 0; i < cnt; i++) {
      ivs.push(new Uint8Array(buf.buffer, buf.byteOffset + off, 8));
      off += 8;
      if (flags & 0x02) {
        const subsampleCount = (buf[off]! << 8) | buf[off + 1]!;
        off += 2;
        const subsamples: { clear: number; enc: number }[] = [];
        for (let j = 0; j < subsampleCount; j++) {
          const clear = (buf[off]! << 8) | buf[off + 1]!;
          const enc = readU32(buf, off + 2);
          subsamples.push({ clear, enc });
          off += 6;
        }
        subsampleMap.push(subsamples);
      }
    }
  }
  return { sampleOffs, sizes, ivs, subsamples: subsampleMap };
}

function parseAllTracks(buf: Uint8Array, moov: Box): Track[] {
  const tracks: Track[] = [];
  for (const trak of iterBoxes(buf, moov.bodyOff, moov.offset + moov.size)) {
    if (trak.tag !== 'trak') continue;
    const mdia = walkPath(buf, trak.bodyOff, trak.offset + trak.size, 'mdia');
    if (!mdia) continue;
    const hdlr = walkPath(buf, mdia.bodyOff, mdia.offset + mdia.size, 'hdlr');
    if (!hdlr) continue;
    const handler = readTag(buf, hdlr.offset + 16);
    if (handler !== 'vide' && handler !== 'soun') continue;
    const stbl = walkPath(buf, mdia.bodyOff, mdia.offset + mdia.size, 'minf', 'stbl');
    if (!stbl) continue;
    const track = parseSampleTable(buf, stbl.bodyOff, stbl.offset + stbl.size);
    if (track && track.ivs.length > 0) tracks.push(track);
  }
  return tracks;
}

function decryptSoft(buf: Uint8Array, track: Track, keyBytes: Uint8Array): void {
  const counter = new Uint8Array(16);
  const cnt = Math.min(track.sampleOffs.length, track.ivs.length);
  for (let i = 0; i < cnt; i++) {
    counter.fill(0);
    counter.set(track.ivs[i]!, 0);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const aesCtr = new (aesjs as any).ModeOfOperation.ctr(
      keyBytes,
      new (aesjs as any).Counter(counter),
    );
    const off = track.sampleOffs[i]!;
    const sz = track.sizes[i]!;
    const sampleSubsamples = track.subsamples[i];
    if (sampleSubsamples && sampleSubsamples.length > 0) {
      let currentOff = off;
      for (const sub of sampleSubsamples) {
        if (sub.clear > 0) {
          const dummy = new Uint8Array(sub.clear);
          aesCtr.decrypt(dummy);
          currentOff += sub.clear;
        }
        if (sub.enc > 0) {
          const subBytes = new Uint8Array(buf.buffer, buf.byteOffset + currentOff, sub.enc);
          const dec = aesCtr.decrypt(subBytes);
          buf.set(dec, currentOff);
          currentOff += sub.enc;
        }
      }
    } else {
      const sub = new Uint8Array(buf.buffer, buf.byteOffset + off, sz);
      const dec = aesCtr.decrypt(sub);
      buf.set(dec, off);
    }
  }
}

function getOriginalVideoCodec(buf: Uint8Array, start: number, end: number): string {
  for (let i = start; i + 8 <= end; i++) {
    if (buf[i] === 102 && buf[i + 1] === 114 && buf[i + 2] === 109 && buf[i + 3] === 97) {
      const codec = String.fromCharCode(buf[i + 4]!, buf[i + 5]!, buf[i + 6]!, buf[i + 7]!);
      if (codec === 'avc1' || codec === 'hvc1' || codec === 'hev1') return codec;
      if (codec === 'byte' || codec === 'btvc') return 'hvc1';
    }
  }
  return 'hvc1';
}

function neutralizeDRM(buf: Uint8Array, moovStart: number, moovEnd: number): void {
  const videoFormat = getOriginalVideoCodec(buf, moovStart, moovEnd);
  const rename: Record<string, string> = {
    encv: videoFormat,
    enca: 'mp4a',
    sinf: 'free',
    schm: 'free',
    schi: 'free',
    senc: 'free',
    saio: 'free',
    saiz: 'free',
    tenc: 'free',
    frma: 'free',
    pssh: 'free',
  };
  for (let i = moovStart + 4; i + 4 <= moovEnd; i++) {
    const tag = readTag(buf, i);
    if (tag in rename) {
      const sz = readU32(buf, i - 4);
      if (sz >= 8 && sz <= moovEnd - i + 4) writeTag(buf, i, rename[tag]!);
    }
  }
  let off = 0;
  while (off + 8 <= buf.length) {
    const sz = readU32(buf, off);
    if (sz < 8 || off + sz > buf.length) break;
    const tag = readTag(buf, off + 4);
    if (tag === 'pssh') writeTag(buf, off + 4, 'free');
    off += sz;
  }
}

/**
 * Decrypt full MP4 buffer in-place & neutralize DRM. Return blob URL siap
 * dipasang ke `<video>.src`. `keyHex` = 32-char hex dari drm.key.
 */
export function decryptMp4CtrToBlobUrl(
  encrypted: ArrayBuffer,
  keyHex: string,
): string {
  const buf = new Uint8Array(encrypted);
  const keyBytes = new Uint8Array(
    keyHex.match(/.{2}/g)!.map((h) => Number.parseInt(h, 16)),
  );
  const moov = walkPath(buf, 0, buf.length, 'moov');
  if (!moov) throw new Error("box 'moov' tidak ditemukan");
  const tracks = parseAllTracks(buf, moov);
  if (tracks.length === 0) throw new Error('tidak ada track terenkripsi');
  for (const t of tracks) decryptSoft(buf, t, keyBytes);
  neutralizeDRM(buf, moov.offset, moov.offset + moov.size);
  const blob = new Blob([buf], { type: 'video/mp4' });
  return URL.createObjectURL(blob);
}
